import Konva from "konva";
import { GetSet } from "konva/lib/types";
import { lvglArcKnobAnchor } from "../Hasp/arcGeometry.js";
import { lvglArcStrokeGeometry } from "./arcGeometry";

interface CustomArcConfig extends Konva.ShapeConfig {
    // x: number;
    // y: number;
    innerRadius: number;
    outerRadius: number;
    angle: number;
    startAngle: number;
    endAngle: number;
    fill?: string;
    // strokeWidth?: number;
    // stroke?: string;
    lineRounded?: string;
    haspRotation?: number; // Rotation parameter to modify internal angles
}

export default class CustomArc extends Konva.Shape {
    private _innerRadius: number;
    private _outerRadius: number;
    private _startAngle: number;
    private _endAngle: number;
    // private _lineWidth: number;
    // private _fill: string;
    private _lineRounded: string;
    private _haspRotation: number; // Now uses haspRotation for angle manipulation
    private _lineWidth: number;
    private _capRadius: number;

    constructor(config: CustomArcConfig) {
        
        super({
            x: config.x,
            y: config.y,
            fill: config.fill || "transparent", // Default fill to transparent if not provided
            // LVGL draws the arc as a filled inward ring. A centred Konva
            // stroke would expand the outer and inner edges by half its width.
            stroke: null,
            strokeWidth: 0,
            drawBorder: false, 
        });

        this._innerRadius = 0;
        this._outerRadius = 0;
        this._lineWidth = 0;
        this._capRadius = 0;
        this.setStrokeRadii(config.outerRadius, config.innerRadius);
        this._startAngle = config.startAngle;
        this._endAngle = config.endAngle;
        // this._lineWidth = config.strokeWidth || 3; // Default to 3 if not provided
        // this._fill = config.fill || "transparent"; // Default to transparent if not provided
        this._lineRounded = config.lineRounded || "round"; // Default to 'round' if not provided
        this._haspRotation = config.haspRotation || 0; // Store haspRotation (default 0 degrees)

        this.sceneFunc((context, shape) => {
            this.drawArc(context, shape);
        });
    }

    /** Apply the haspRotation to start and end angles without modifying the initial ones */
    private getAdjustedStartAngle(): number {
        return this._startAngle + this._haspRotation;
    }

    private getAdjustedEndAngle(): number {
        return this._endAngle + this._haspRotation;
    }

    /** Konva-style getter/setter for inner radius */
    innerRadius(newRadius?: number): number | this {
        if (newRadius === undefined) return this._innerRadius; // Getter
        this.setStrokeRadii(this._outerRadius, newRadius);
        this.redrawShape();
        return this;
    }

    /** Konva-style getter/setter for outer radius */
    outerRadius(newRadius?: number): number | this {
        if (newRadius === undefined) return this._outerRadius; // Getter
        this.setStrokeRadii(newRadius, this._innerRadius);
        this.redrawShape();
        return this;
    }

    /** Konva-style getter/setter for start angle */
    startAngle(newStartAngle?: number): number | this {
        if (newStartAngle === undefined) return this._startAngle; // Getter
        this._startAngle = newStartAngle;
        this.redrawShape();
        return this;
    }

    /** Konva-style getter/setter for end angle */
    endAngle(newEndAngle?: number): number | this {
        if (newEndAngle === undefined) return this._endAngle; // Getter
        this._endAngle = newEndAngle;
        this.redrawShape();
        return this;
    }

    private calculateArcAngle(startAngle, endAngle) {
        let counterclockwiseAngle = (endAngle - startAngle + 360) % 360;
        let clockwiseAngle = (360 - counterclockwiseAngle) % 360;

        return { counterclockwise: counterclockwiseAngle, clockwise: clockwiseAngle };
    }

    /** ✅ Konva-style getter/setter for angle */
    angle(newAngle?: number): number | this {
        if (newAngle === undefined) { 
            const angles = this.calculateArcAngle(this._startAngle, this._endAngle);
            // console.log(angles)
            return angles.counterclockwise;
        }
        this._endAngle = this._startAngle + newAngle;
        this.redrawShape();
        return this;
    }

    /** Konva-style getter/setter for haspRotation */
    haspRotation(newRotation?: number): number | this {
        if (newRotation === undefined) return this._haspRotation; // Getter
        this._haspRotation = newRotation
        this.redrawShape(); // Update the arc with the new rotation
        return this;
    }

    /** Konva-style getter/setter for haspRotation */
    lineRounded(newValue?: string): string | this {
        if (newValue === undefined) return this._lineRounded; // Getter
        this._lineRounded = newValue;
        this.redrawShape(); // Update the arc with the new rotation
        return this;
    }

    /** Draw the arc shape */
    private drawArc(context: Konva.Context, shape: Konva.Shape): void {

        // Convert adjusted angles to radians
        const startAngle = (Math.PI / 180) * this.getAdjustedStartAngle();
        const endAngle = (Math.PI / 180) * this.getAdjustedEndAngle();

        // Draw the inward LVGL ring as fill geometry. Do not use a native
        // Konva stroke: its centred semantics are not LVGL's inside-border
        // semantics and would enlarge both visible edges.
        context.beginPath();
        context.arc(0, 0, Math.abs(this._outerRadius), startAngle, endAngle, false);
        context.arc(0, 0,  Math.abs(this._innerRadius), endAngle, startAngle, true);
        context.closePath();
        context.fillShape(shape);

        // LVGL's rounded endpoint center is radius - trunc(width / 2),
        // matching the knob anchor calculation exactly.
        if (this._lineRounded === 'round' && this._lineWidth > 0) {
            this.drawCap(context, shape, startAngle);
            this.drawCap(context, shape, endAngle);
        }
    }

    /** Draw the rounded cap at the specified angle */
    private drawCap(context: Konva.Context, shape: Konva.Shape, angle: number): void {
        const capCenter = lvglArcKnobAnchor({
            center: { x: 0, y: 0 },
            radius: this._outerRadius,
            indicatorLineWidth: this._lineWidth,
            angle: angle * 180 / Math.PI,
        });

        context.beginPath();
        context.arc(capCenter.x, capCenter.y, this._capRadius, 0, Math.PI * 2, false);
        context.closePath();
        context.fillShape(shape);
    }

    /** Resolve LVGL's inward-ring dimensions after either radius changes. */
    private setStrokeRadii(outerRadius: number, innerRadius: number): void {
        const geometry = lvglArcStrokeGeometry({
            radius: outerRadius,
            lineWidth: Number(outerRadius) - Number(innerRadius),
        });
        this._outerRadius = geometry.outerRadius;
        this._innerRadius = geometry.innerRadius;
        this._lineWidth = geometry.lineWidth;
        this._capRadius = geometry.capRadius;
    }

    /** Redraw the shape with updated properties */
    private redrawShape(): void {
        this.getLayer()?.batchDraw(); // Ensure the layer is updated after changes
    }
}
